import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { StarRating } from "./StarRating";
import { Trash2, Upload, X, ImageIcon, ShieldCheck } from "lucide-react";

const MAX_REVIEW_PHOTOS = 4;

export default function ProductReviews({ product, onChange }) {
  const { user } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [form, setForm] = useState({ rating: 0, title: "", body: "", images: [] });
  const [lightbox, setLightbox] = useState(null);

  const load = () => {
    setLoading(true);
    api.get(`/products/${product.id}/reviews`)
      .then(({ data }) => setReviews(data))
      .catch(() => setReviews([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (product?.id) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  const myReview = user && reviews.find((r) => r.user_id === user.id);
  const count = reviews.length;
  const avg = count > 0 ? reviews.reduce((s, r) => s + r.rating, 0) / count : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!form.rating) {
      toast.error("Pick a star rating first ✨");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews", {
        product_id: product.id,
        rating: form.rating,
        title: form.title.trim(),
        body: form.body.trim(),
        images: form.images,
      });
      toast.success("Thanks for the love babe!");
      setForm({ rating: 0, title: "", body: "", images: [] });
      setShowForm(false);
      load();
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't post review");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReviewImage = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (form.images.length >= MAX_REVIEW_PHOTOS) {
      toast.error(`Up to ${MAX_REVIEW_PHOTOS} photos per review`);
      return;
    }
    if (file.size > 8 * 1024 * 1024) { toast.error("Photo must be under 8MB"); return; }
    if (!file.type.startsWith("image/")) { toast.error("Pick an image file"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/review-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, images: [...f.images, data.url] }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const removeReviewImage = (idx) => {
    setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));
  };

  const remove = async (id) => {
    if (!window.confirm("Delete your review?")) return;
    try {
      await api.delete(`/reviews/${id}`);
      toast.success("Review removed");
      load();
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't delete");
    }
  };

  return (
    <section className="mt-20 max-w-3xl" data-testid="product-reviews">
      <div className="font-script text-3xl text-[#2eb6a8]">What Babes Are Saying</div>
      <h2 className="font-display text-3xl sm:text-4xl font-black text-[#1c1815] tracking-tight">Reviews</h2>

      <div className="mt-5 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <StarRating value={avg} size={18} testid="reviews-avg-stars"/>
          <span className="font-bold text-[#1c1815]" data-testid="reviews-avg-text">
            {count > 0 ? avg.toFixed(1) : "—"}
          </span>
          <span className="text-sm text-[#8a8076]" data-testid="reviews-count">({count} review{count === 1 ? "" : "s"})</span>
        </div>

        {!myReview && user && !showForm && (
          <button
            data-testid="open-review-form"
            onClick={() => setShowForm(true)}
            className="ml-auto bg-[#e8478e] hover:bg-[#c93875] hover:shadow-lg hover:-translate-y-0.5 text-white text-[10px] uppercase tracking-[0.2em] font-bold px-5 py-2.5 rounded-full transition-all"
          >
            Write a Review
          </button>
        )}
        {!user && (
          <Link to="/login" data-testid="reviews-login-prompt"
            className="ml-auto text-xs text-[#8a8076] underline underline-offset-4 hover:text-[#1c1815]">
            Log in to write a review
          </Link>
        )}
      </div>

      {showForm && user && !myReview && (
        <form onSubmit={submit} data-testid="review-form" className="mt-6 p-6 rounded-2xl border border-[#e5dccd] bg-white space-y-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold mb-2">Your Rating</div>
            <StarRating value={form.rating} size={26} interactive onChange={(v) => setForm({ ...form, rating: v })} testid="review-rating"/>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">Title</label>
            <input
              required maxLength={120}
              data-testid="review-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Sum it up in a few words"
              className="mt-1.5 w-full bg-[#faf4e9] border border-[#e5dccd] focus:border-[#2eb6a8] focus:ring-2 focus:ring-[#2eb6a8]/20 rounded-xl px-4 py-3 outline-none text-[#1c1815]"
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">Your Review</label>
            <textarea
              required rows={4} maxLength={3000}
              data-testid="review-body"
              value={form.body}
              onChange={(e) => setForm({ ...form, body: e.target.value })}
              placeholder="Tell us everything ✨"
              className="mt-1.5 w-full bg-[#faf4e9] border border-[#e5dccd] focus:border-[#2eb6a8] focus:ring-2 focus:ring-[#2eb6a8]/20 rounded-xl px-4 py-3 outline-none resize-y text-[#1c1815]"
            />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="text-[10px] uppercase tracking-[0.25em] text-[#8a8076] font-bold">Add Photos (optional)</label>
              <span className="text-[10px] text-[#8a8076]" data-testid="review-photo-count">{form.images.length} / {MAX_REVIEW_PHOTOS}</span>
            </div>
            <p className="text-[11px] text-[#8a8076] mt-0.5">Show off your goodies! Up to {MAX_REVIEW_PHOTOS} photos, 8MB each.</p>
            {form.images.length > 0 && (
              <div className="mt-2 grid grid-cols-4 gap-2" data-testid="review-photo-grid">
                {form.images.map((url, idx) => (
                  <div key={url + idx} className="relative group aspect-square">
                    <img src={url} alt="" className="w-full h-full object-cover rounded-xl border border-[#e5dccd]"/>
                    <button type="button" onClick={() => removeReviewImage(idx)}
                      className="absolute top-1 right-1 bg-[#c93875] hover:bg-[#e8478e] text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity shadow"
                      data-testid={`review-photo-remove-${idx}`}
                      aria-label="Remove photo">
                      <X size={12}/>
                    </button>
                  </div>
                ))}
              </div>
            )}
            {form.images.length < MAX_REVIEW_PHOTOS && (
              <label className={`mt-2 inline-flex items-center gap-2 cursor-pointer text-sm bg-[#faf4e9] hover:bg-[#efe5d4] px-4 py-2.5 rounded-xl border border-[#e5dccd] text-[#1c1815] ${uploading ? "opacity-60 pointer-events-none" : ""}`} data-testid="review-photo-upload-label">
                <Upload size={14}/> {uploading ? "Uploading…" : "Add a photo"}
                <input type="file" accept="image/*" className="hidden" data-testid="review-photo-input" onChange={handleReviewImage} disabled={uploading}/>
              </label>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button type="submit" disabled={submitting} data-testid="review-submit"
              className="bg-[#2eb6a8] hover:bg-[#1f8d83] hover:shadow-lg hover:-translate-y-0.5 text-white font-bold uppercase tracking-[0.2em] text-xs px-6 py-3 rounded-full transition-all disabled:opacity-60">
              {submitting ? "Posting…" : "Post Review"}
            </button>
            <button type="button" onClick={() => setShowForm(false)} className="text-xs text-[#8a8076] hover:text-[#1c1815] uppercase tracking-[0.2em] font-bold">
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="mt-8 space-y-5" data-testid="reviews-list">
        {loading && <div className="text-[#8a8076] text-sm">Loading reviews…</div>}
        {!loading && count === 0 && (
          <div className="p-6 rounded-2xl border border-dashed border-[#e5dccd] text-center">
            <div className="font-script text-2xl text-[#e8478e]">Be the first</div>
            <p className="text-sm text-[#8a8076] mt-1">No reviews yet. Drop the first one and start the love.</p>
          </div>
        )}
        {!loading && reviews.map((r) => (
          <div key={r.id} data-testid={`review-${r.id}`} className="p-5 rounded-2xl border border-[#e5dccd] bg-white">
            <div className="flex items-start justify-between gap-3">
              <div>
                <StarRating value={r.rating} size={14}/>
                <div className="font-bold text-[#1c1815] mt-1.5">{r.title}</div>
                <div className="text-xs text-[#8a8076] mt-0.5 flex items-center flex-wrap gap-x-1.5 gap-y-0.5">
                  <span>{r.user_name}</span>
                  <span>·</span>
                  <span>{new Date(r.created_at).toLocaleDateString()}</span>
                  {r.verified_buyer && (
                    <span className="inline-flex items-center gap-1 bg-[#2eb6a8]/15 border border-[#2eb6a8]/40 text-[#1f8d83] text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-full" data-testid={`verified-buyer-${r.id}`}>
                      <ShieldCheck size={10}/> Verified Buyer
                    </span>
                  )}
                </div>
              </div>
              {user && (user.id === r.user_id || user.role === "admin") && (
                <button onClick={() => remove(r.id)} title="Delete" data-testid={`review-delete-${r.id}`}
                  className="text-[#8a8076] hover:text-[#c93875] p-1">
                  <Trash2 size={14}/>
                </button>
              )}
            </div>
            <p className="mt-3 text-sm text-[#4a4239] leading-relaxed whitespace-pre-line">{r.body}</p>
            {Array.isArray(r.images) && r.images.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2" data-testid={`review-images-${r.id}`}>
                {r.images.map((url, idx) => (
                  <button
                    key={url + idx}
                    type="button"
                    onClick={() => setLightbox(url)}
                    data-testid={`review-image-${r.id}-${idx}`}
                    className="w-20 h-20 sm:w-24 sm:h-24 rounded-xl overflow-hidden border border-[#e5dccd] hover:border-[#e8478e] hover:shadow-md transition-all"
                    aria-label="View photo"
                  >
                    <img src={url} alt="" className="w-full h-full object-cover"/>
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {lightbox && (
        <div
          data-testid="review-lightbox"
          onClick={() => setLightbox(null)}
          className="fixed inset-0 z-[80] bg-[#1c1815]/85 backdrop-blur-sm flex items-center justify-center p-6 cursor-zoom-out"
        >
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute top-5 right-5 bg-white/15 hover:bg-white/25 text-white rounded-full p-2 transition-colors"
            aria-label="Close"
            data-testid="review-lightbox-close"
          >
            <X size={20}/>
          </button>
          <img src={lightbox} alt="" className="max-w-full max-h-full object-contain rounded-xl shadow-2xl" onClick={(e) => e.stopPropagation()}/>
        </div>
      )}
    </section>
  );
}
